import React, { useEffect, useState, useCallback } from 'react';
import { Activity, Calendar, Trash2, Megaphone, CheckSquare, AlertTriangle, RefreshCw, Loader2, Zap } from 'lucide-react';
import api from '../lib/api';
import { Layout } from '../components/Layout';

const ACTION_CONFIG = {
  shift_create:        { label: 'Shift Created',       color: 'bg-teal-100 text-teal-700',    Icon: Calendar    },
  shift_delete:        { label: 'Shift Deleted',       color: 'bg-red-100 text-red-700',      Icon: Trash2      },
  shift_override:      { label: 'Override',            color: 'bg-amber-100 text-amber-700',  Icon: AlertTriangle },
  task_create:         { label: 'Task Created',        color: 'bg-violet-100 text-violet-700',Icon: CheckSquare },
  task_delete:         { label: 'Task Deleted',        color: 'bg-red-100 text-red-700',      Icon: Trash2      },
  announcement_create: { label: 'Announcement',        color: 'bg-blue-100 text-blue-700',    Icon: Megaphone   },
};

const ALL_TYPES = ['all', ...Object.keys(ACTION_CONFIG)];

function formatMeta(action_type, meta) {
  if (!meta) return '—';
  switch (action_type) {
    case 'shift_create':
    case 'shift_delete':
    case 'shift_override':
      return [meta.date, meta.start_time && meta.end_time ? `${meta.start_time}–${meta.end_time}` : null]
        .filter(Boolean).join(' · ') || '—';
    case 'task_create':
    case 'task_delete':
      return meta.title || '—';
    case 'announcement_create':
      return meta.title || '—';
    default:
      return JSON.stringify(meta);
  }
}

function ActionBadge({ type }) {
  const cfg = ACTION_CONFIG[type] || { label: type, color: 'bg-slate-100 text-slate-600', Icon: Activity };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <cfg.Icon size={10} />
      {cfg.label}
    </span>
  );
}

export default function AuditLog() {
  const [logs, setLogs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  const fetchLogs = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const params = filter !== 'all' ? { action_type: filter } : {};
      const res = await api.get('/eva/audit-log', { params });
      setLogs(res.data?.logs || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filtered = logs;

  return (
    <Layout>
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="audit-log-page">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
            <Zap size={18} className="text-orange-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Eva Activity Log</h1>
            <p className="text-xs text-slate-500">All Eva-triggered actions across your team</p>
          </div>
        </div>
        <button
          onClick={() => fetchLogs(true)}
          disabled={refreshing}
          data-testid="audit-log-refresh-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="audit-log-filters">
        {ALL_TYPES.map(type => {
          const cfg = type === 'all' ? null : ACTION_CONFIG[type];
          const active = filter === type;
          return (
            <button
              key={type}
              onClick={() => setFilter(type)}
              data-testid={`audit-filter-${type}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                active
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {type === 'all' ? 'All Actions' : (cfg?.label || type)}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 size={24} className="animate-spin mr-2" />
            Loading activity log…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <Activity size={32} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">No activity yet</p>
            <p className="text-xs mt-1">Eva actions will appear here once created</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-log-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-44">Time</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">User</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Action</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map((log, i) => (
                  <tr key={i} className="hover:bg-slate-50 transition-colors" data-testid="audit-log-row">
                    <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800 text-sm leading-tight">{log.user_name || '—'}</p>
                      <p className="text-xs text-slate-400 capitalize">{log.user_role || ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <ActionBadge type={log.action_type} />
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs max-w-xs truncate">
                      {formatMeta(log.action_type, log.metadata)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-slate-400 text-center mt-3">{filtered.length} entries · most recent first</p>
      )}
    </div>
    </Layout>
  );
}
